import { useState } from "react";
import { PASS_MARK, type AssessmentView, type ReviewTask } from "../../api/assessment";
import type { ChecklistItem } from "../../api/content";
import { BackButton } from "../../components/BackButton";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Checks } from "../../components/Checks";
import { inputShell } from "../../components/Field";
import { Guide } from "../../components/Guide";
import { OptionTile } from "../../components/OptionTile";
import { ScreenHeader } from "../../components/ScreenHeader";
import { safeHref } from "../../lib/format";

const KICKER = "Step 4 · Readiness assessment";

/* ---------- self-check ---------- */

export function SelfCheckScreen({
  view,
  checklist,
  busy,
  onSave,
}: {
  view: AssessmentView;
  checklist: ChecklistItem[];
  busy: boolean;
  onSave: (keys: string[]) => void;
}) {
  const [keys, setKeys] = useState(new Set(view.selfCheck));
  return (
    <div>
      <ScreenHeader
        kicker={KICKER}
        title="Confirm your checklist, honestly."
        sub={
          <>
            Open{" "}
            <a
              href={safeHref(view.url)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent underline"
            >
              your portfolio
            </a>{" "}
            beside this and check only what's genuinely there. Your peers check the same list when
            they review you.
          </>
        }
      />
      <div className="flex flex-col gap-2">
        {checklist.map((item) => (
          <OptionTile
            key={item.key}
            selected={keys.has(item.key)}
            title={item.t}
            detail={item.d}
            onToggle={() => {
              const next = new Set(keys);
              if (next.has(item.key)) next.delete(item.key);
              else next.add(item.key);
              setKeys(next);
            }}
          />
        ))}
      </div>
      <p className="mt-2.5 text-[13px] text-text-muted">
        {keys.size} of {checklist.length} checked
      </p>
      <div className="flex flex-wrap gap-2.5">
        <BackButton to="/gig/4" />
        <Button disabled={busy} onClick={() => onSave(Array.from(keys))}>
          Save and continue →
        </Button>
      </div>
    </div>
  );
}

/* ---------- review hub ---------- */

export function ReviewHubScreen({
  view,
  onStart,
  onRefresh,
}: {
  view: AssessmentView;
  onStart: () => void;
  onRefresh: () => void;
}) {
  if (view.reviewsAssigned === 0) {
    return (
      <div>
        <ScreenHeader
          kicker={KICKER}
          title="You're in the queue."
          sub="Your portfolio is in your track's review pool. Peer review starts as soon as at least one other person in your track has submitted. Check back shortly, and finish the other Step 4 parts meanwhile."
        />
        <div className="flex flex-wrap gap-2.5">
          <Button onClick={onRefresh}>Refresh ↻</Button>
          <BackButton to="/gig/4" label="Back to Step 4" />
        </div>
      </div>
    );
  }
  return (
    <div>
      <ScreenHeader
        kicker={KICKER}
        title={`Review ${view.reviewsAssigned} portfolio${view.reviewsAssigned > 1 ? "s" : ""} to unlock your score.`}
        sub={`You've done ${view.reviewsDone} of ${view.reviewsAssigned}. Same track as you, fully anonymous. Score honestly, it's how everyone gets a fair read.`}
      />
      <Guide guideKey="review" done={false} />
      <div
        className="my-4 flex gap-1.5"
        aria-label={`${view.reviewsDone} of ${view.reviewsAssigned} reviews done`}
      >
        {Array.from({ length: view.reviewsAssigned }, (_, i) => (
          <i
            key={i}
            className={`block h-2.5 flex-1 rounded-full ${
              i < view.reviewsDone
                ? "bg-positive"
                : i === view.reviewsDone
                  ? "bg-accent"
                  : "bg-surface-high"
            }`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-2.5">
        <Button onClick={onStart}>
          {view.reviewsDone ? "Continue reviewing" : "Start reviewing"} →
        </Button>
        <BackButton to="/gig/4" label="Back to Step 4" />
      </div>
    </div>
  );
}

/* ---------- one review ---------- */

export function ReviewScreen({
  task,
  position,
  total,
  checklist,
  busy,
  error,
  onSubmit,
  onPause,
}: {
  task: ReviewTask;
  position: number;
  total: number;
  checklist: ChecklistItem[];
  busy: boolean;
  error: string | null;
  onSubmit: (scores: Record<string, number>, checklist: string[]) => void;
  onPause: () => void;
}) {
  const [index, setIndex] = useState(0);
  const [scores, setScores] = useState<Record<string, number>>({});
  const [seen, setSeen] = useState<Set<string>>(new Set());
  const onChecklist = index === task.rubric.length;
  const question = task.rubric[index];

  const header = (
    <Banner tone="note">
      <b>Reviewing · anonymous</b>
      <br />
      <a
        href={safeHref(task.url)}
        target="_blank"
        rel="noopener noreferrer"
        className="break-all text-accent underline"
      >
        {task.url} ↗
      </a>
    </Banner>
  );

  if (onChecklist || !question) {
    return (
      <div>
        <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-kicker">
          Step 4 · Peer review
        </div>
        {header}
        <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-kicker">
          Review {position} of {total} · The 10-point checklist
        </div>
        <h1 className="mb-2 text-[26px] text-accent">What can you actually see on their page?</h1>
        <p className="mb-4 text-text-muted">
          Check only what's genuinely there, as a client would.
        </p>
        <div className="flex flex-col gap-2">
          {checklist.map((item) => (
            <OptionTile
              key={item.key}
              selected={seen.has(item.key)}
              title={item.t}
              detail={item.d}
              onToggle={() => {
                const next = new Set(seen);
                if (next.has(item.key)) next.delete(item.key);
                else next.add(item.key);
                setSeen(next);
              }}
            />
          ))}
        </div>
        {error ? (
          <p role="alert" className="mt-2 text-[13.5px] text-negative">
            {error}
          </p>
        ) : null}
        <div className="mt-4 flex flex-wrap gap-2.5">
          <Button variant="ghost" onClick={() => setIndex(task.rubric.length - 1)}>
            ← Back
          </Button>
          <Button disabled={busy} onClick={() => onSubmit(scores, Array.from(seen))}>
            {busy ? "Submitting..." : "Submit review →"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-kicker">
        Step 4 · Peer review
      </div>
      {header}
      <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-kicker">
        Review {position} of {total} · Question {index + 1} of {task.rubric.length}
        {question.w > 1 ? " · counts double" : ""}
      </div>
      <h1 className="mb-2 text-[26px] text-accent">{question.q}</h1>
      <p className="mb-4 text-text-muted">{question.prompt}</p>
      <div role="radiogroup" aria-label={question.q} className="flex gap-2">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={scores[question.key] === n}
            onClick={() => setScores({ ...scores, [question.key]: n })}
            className={`h-12 flex-1 rounded-[var(--radius-control)] border-[1.5px] text-[17px] font-bold ${
              scores[question.key] === n
                ? "border-accent bg-accent text-on-accent"
                : "border-border bg-surface"
            }`}
          >
            {n}
          </button>
        ))}
      </div>
      <div className="mt-1.5 flex justify-between text-[12px] text-text-muted">
        <span>1, {question.lo}</span>
        <span>{question.hi}, 5</span>
      </div>
      <div className="mt-5 flex flex-wrap gap-2.5">
        {index > 0 ? (
          <Button variant="ghost" onClick={() => setIndex(index - 1)}>
            ← Back
          </Button>
        ) : (
          <Button variant="ghost" onClick={onPause}>
            ← Pause
          </Button>
        )}
        <Button disabled={!scores[question.key]} onClick={() => setIndex(index + 1)}>
          {index === task.rubric.length - 1 ? "To the checklist" : "Next"} →
        </Button>
      </div>
    </div>
  );
}

/* ---------- result ---------- */

export function ResultScreen({
  view,
  onReview,
  onResubmit,
  onRefresh,
}: {
  view: AssessmentView;
  onReview: () => void;
  onResubmit: () => void;
  onRefresh: () => void;
}) {
  const reviewsLeft = view.reviewsAssigned - view.reviewsDone;
  const gateOpen = view.reviewsAssigned > 0 && reviewsLeft <= 0;

  let score: React.ReactNode;
  if (!view.structPass)
    score = (
      <>
        <div className="text-[28px] font-extrabold text-text-muted">Not yet</div>
        <p className="text-[13px]">Fix your portfolio link first.</p>
      </>
    );
  else if (!gateOpen)
    score = (
      <>
        <div className="text-[28px] font-extrabold text-text-muted">Locked</div>
        <p className="text-[13px]">
          {view.reviewsAssigned === 0
            ? "Waiting for someone else in your track to submit."
            : `Locked, finish your ${reviewsLeft} remaining review${reviewsLeft > 1 ? "s" : ""} to unlock.`}
        </p>
      </>
    );
  else if (view.qualityPct == null)
    score = (
      <>
        <div className="text-[28px] font-extrabold text-text-muted">Pending</div>
        <p className="text-[13px]">
          Your reviews are done. {view.reviewsReceived} of {view.reviewsNeeded} peer reviews of your
          work are in.
        </p>
      </>
    );
  else
    score = (
      <>
        <div className="text-[34px] font-extrabold text-accent tabular">{view.qualityPct}%</div>
        <p className="text-[13px]">
          {view.qualityPct >= PASS_MARK
            ? `Above the ${PASS_MARK}% mark.`
            : `Below the ${PASS_MARK}% mark, room to grow.`}
        </p>
      </>
    );

  return (
    <div>
      <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.14em] text-kicker">
        {KICKER}
      </div>
      <div className="mb-4">
        <div className="text-[12px] font-bold uppercase text-text-muted">Verdict</div>
        <h2 className="text-[26px] text-accent">{view.verdictTitle}</h2>
        <p className="m-0 text-text-muted">{view.verdictText}</p>
      </div>
      {view.structPass && !gateOpen && view.reviewsAssigned > 0 ? (
        <Banner tone="note">
          <b>Your Quality Score is waiting on you.</b> You have {reviewsLeft} review
          {reviewsLeft > 1 ? "s" : ""} left. Reviewing others is how you unlock your own score.
        </Banner>
      ) : null}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <small className="text-[11px] font-bold uppercase text-text-muted">
            Structural check
          </small>
          <div className="text-[22px] font-extrabold">{view.structPass ? "Passed" : "Not yet"}</div>
          <Checks checks={view.structChecks} />
        </div>
        <div className="rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <small className="text-[11px] font-bold uppercase text-text-muted">
            Quality Score{view.qualityPct != null ? `, avg of ${view.reviewsReceived}` : ""}
          </small>
          {score}
        </div>
      </div>
      {view.perQuestion.length ? (
        <div className="my-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
          <h3 className="mb-3 text-[16px]">How your peers scored the work</h3>
          {view.perQuestion.map((q) => (
            <div key={q.key} className="mb-2 flex items-center gap-3 text-[13.5px]">
              <span className="flex-1">
                {q.q}
                {q.w > 1 ? <b className="text-accent"> x2</b> : null}
              </span>
              <span className="h-2 w-28 overflow-hidden rounded-full bg-surface-high">
                <i className="block h-full bg-accent" style={{ width: `${(q.avg / 5) * 100}%` }} />
              </span>
              <span className="w-8 text-right tabular">{q.avg.toFixed(1)}</span>
            </div>
          ))}
          <p className="mt-2 text-[12.5px] text-text-muted">
            Averaged across {view.reviewsReceived} anonymous reviewer
            {view.reviewsReceived > 1 ? "s" : ""}, each score out of 5.
          </p>
        </div>
      ) : null}
      <div className="my-4 rounded-[var(--radius-card)] border border-border bg-surface p-4">
        <h3 className="mb-3 text-[16px]">
          {view.reviewsReceived
            ? "Your checklist vs your peers"
            : "Your self-checklist (peers will confirm)"}
        </h3>
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[11px] text-text-muted">
              <th className="py-1 font-normal">Checklist item</th>
              <th className="py-1 text-center font-normal">You said</th>
              <th className="py-1 text-right font-normal">Peers</th>
            </tr>
          </thead>
          <tbody>
            {view.checklist.map((item) => (
              <tr key={item.key} className="border-t border-border">
                <td className="py-1.5">{item.t}</td>
                <td
                  className={`py-1.5 text-center ${item.youSaid ? "text-positive" : "text-text-muted"}`}
                >
                  {item.youSaid ? "✓ you" : "–"}
                </td>
                <td
                  className={`py-1.5 text-right tabular ${item.disagree ? "font-bold text-negative" : "text-accent"}`}
                >
                  {item.peersTotal ? `${item.peersYes}/${item.peersTotal}` : "–"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-2 text-[12.5px] text-text-muted">
          {view.reviewsReceived
            ? "Where peers disagree with your check mark, it shows in red, a sign to look again."
            : "Peers verify these same points when they review you."}
        </p>
      </div>
      {view.pass ? (
        <Banner tone="good">
          ✓ Assessment passed. Finish any remaining Step 4 parts to become Work-Enabled.
        </Banner>
      ) : null}
      <div className="flex flex-wrap gap-2.5">
        {view.structPass && !gateOpen && view.reviewsAssigned > 0 ? (
          <Button onClick={onReview}>Finish my reviews →</Button>
        ) : null}
        {view.canResubmit ? <Button onClick={onResubmit}>Improve and resubmit →</Button> : null}
        {view.verdictKey === "pending" ? (
          <Button variant="ghost" onClick={onRefresh}>
            Refresh ↻
          </Button>
        ) : null}
        <BackButton to="/gig/4" label="Back to Step 4" />
      </div>
    </div>
  );
}

/* ---------- resubmit ---------- */

export function ResubmitScreen({
  currentUrl,
  busy,
  error,
  onSubmit,
  onBack,
}: {
  currentUrl: string;
  busy: boolean;
  error: string | null;
  onSubmit: (url: string, confirmed: boolean) => void;
  onBack: () => void;
}) {
  const [url, setUrl] = useState(currentUrl);
  const [confirmed, setConfirmed] = useState(false);
  return (
    <div>
      <ScreenHeader
        kicker="Step 4 · Improve and resubmit"
        title="Strengthen it, then get a fresh review."
        sub="Work on the lowest-scored areas and the checklist items peers didn't see. Then paste the updated link (it can be the same link if you edited the page). Your earlier scores are cleared and peers review the new version."
      />
      <label className="mb-4 block text-[12.5px] font-bold">
        Your improved portfolio link
        <input
          type="url"
          className={`${inputShell} mt-1.5`}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </label>
      <OptionTile
        selected={confirmed}
        onToggle={() => setConfirmed(!confirmed)}
        title="I've made real improvements to the work"
        detail="Resubmitting without changes wastes your peers' time"
      />
      {error ? (
        <p role="alert" className="mt-2 text-[13.5px] text-negative">
          {error}
        </p>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2.5">
        <Button
          disabled={busy || !confirmed || !url.trim()}
          onClick={() => onSubmit(url.trim(), confirmed)}
        >
          Resubmit for review →
        </Button>
        <Button variant="ghost" onClick={onBack}>
          ← Back
        </Button>
      </div>
    </div>
  );
}
